import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/pages/public/${slug}`, { skipAuth: true });
      setPage(response);
      
      // Set page title and meta tags
      if (response) {
        document.title = response.seo_title || response.title;
        
        // Set meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription && response.seo_description) {
          metaDescription.setAttribute('content', response.seo_description);
        }
      }
    } catch (error) {
      console.error('Failed to load page:', error);
      setError('Страницата не е намерена');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (richJson) => {
    if (!richJson || !richJson.content) return null;

    return richJson.content.map((node, index) => {
      switch (node.type) {
        case 'heading':
          const HeadingTag = `h${node.attrs?.level || 2}`;
          const headingClasses = {
            1: 'text-4xl md:text-5xl font-playfair font-bold mb-6',
            2: 'text-3xl md:text-4xl font-playfair font-bold mb-5',
            3: 'text-2xl md:text-3xl font-playfair font-bold mb-4',
          };
          return (
            <HeadingTag key={index} className={headingClasses[node.attrs?.level] || headingClasses[2]}>
              {node.content?.map((c, i) => c.text).join('')}
            </HeadingTag>
          );

        case 'paragraph':
          return (
            <p key={index} className="mb-4 leading-relaxed text-neutral-300">
              {node.content?.map((c, i) => {
                if (c.type === 'text') {
                  let content = c.text;
                  if (c.marks) {
                    c.marks.forEach(mark => {
                      if (mark.type === 'bold') {
                        content = <strong key={i} className="font-semibold">{content}</strong>;
                      }
                      if (mark.type === 'italic') {
                        content = <em key={i}>{content}</em>;
                      }
                      if (mark.type === 'link') {
                        content = (
                          <a
                            key={i}
                            href={mark.attrs?.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-gold hover:text-accent-gold-light underline"
                          >
                            {content}
                          </a>
                        );
                      }
                    });
                  }
                  return <span key={i}>{content}</span>;
                }
                return null;
              })}
            </p>
          );

        case 'bulletList':
          return (
            <ul key={index} className="list-disc list-inside mb-4 space-y-2 text-neutral-300">
              {node.content?.map((item, i) => (
                <li key={i}>
                  {item.content?.[0]?.content?.map((c) => c.text).join('')}
                </li>
              ))}
            </ul>
          );

        case 'orderedList':
          return (
            <ol key={index} className="list-decimal list-inside mb-4 space-y-2 text-neutral-300">
              {node.content?.map((item, i) => (
                <li key={i}>
                  {item.content?.[0]?.content?.map((c) => c.text).join('')}
                </li>
              ))}
            </ol>
          );

        case 'blockquote':
          return (
            <blockquote key={index} className="border-l-4 border-accent-gold pl-6 py-2 mb-4 italic text-neutral-400">
              {node.content?.map((p, i) => (
                <p key={i}>{p.content?.map((c) => c.text).join('')}</p>
              ))}
            </blockquote>
          );

        case 'image':
          return (
            <div key={index} className="mb-6">
              <img
                src={node.attrs?.src}
                alt={node.attrs?.alt || ''}
                className="w-full rounded-lg"
              />
            </div>
          );

        default:
          return null;
      }
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-primary-black flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent-gold" />
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-primary-black flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-playfair font-bold text-neutral-200 mb-4">404</h1>
            <p className="text-neutral-400">{error}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-primary-black pt-32 pb-20">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold mb-4 bg-gradient-to-r from-accent-gold via-accent-gold-light to-accent-gold bg-clip-text text-transparent">
              {page.title}
            </h1>
            {page.excerpt && (
              <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
                {page.excerpt}
              </p>
            )}
            <div className="flex items-center justify-center gap-4 mt-6 text-sm text-neutral-500">
              <time dateTime={page.published_at}>
                {new Date(page.published_at).toLocaleDateString('bg-BG', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            </div>
          </header>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            {page.body_rich_json && renderContent(page.body_rich_json)}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
